import type { EarnedAchievement, SessionItem, TrainingSession } from '../api/models';

/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Session/.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 */

/** Where one task is in its life. Drives the action bar label and whether options accept taps. */
export type TaskPhase =
  /** Waiting for the child to choose. */
  | 'answering'
  /** Submitted, waiting for the server. */
  | 'submitting'
  /** Answered wrongly, attempts remain. A hint is showing. */
  | 'retrying'
  /** Closed correctly. */
  | 'solved'
  /** Closed after the third attempt, or expired. An explanation is showing. */
  | 'closed';

/**
 * How the feedback panel should look. Deliberately has **no "error" value**: BR-13 forbids telling a child
 * they were wrong, so a wrong answer is `hint`, which the design system renders in amber on the hint —
 * never on the child's choice, and never in red.
 */
export type FeedbackTone = 'affirm' | 'hint' | 'neutral';

/**
 * Everything a task screen renders, and nothing else.
 *
 * `feedbackText` is always a string the server sent. Nothing here is composed locally.
 */
export type TaskState = {
  item: SessionItem;
  phase: TaskPhase;
  selectedOptionKeys: string[];
  typedText: string;
  ordering: string[];
  feedbackText: string | null;
  feedbackTone: FeedbackTone;
  attemptsRemaining: number;
};

/**
 * A task, in the phase the **server** says it is in.
 *
 * `phase: 'answering'` unconditionally was wrong, and R0049's keyboard run found it: re-opening a session
 * already finished today rendered a solved task as answerable, and submitting it earned the child
 * "Túto akciu sa teraz nedá dokončiť." The API had said `status: "completed"` and the client ignored it.
 */
export const taskStateFor = (item: SessionItem): TaskState => ({
  item,
  phase: item.status === 'open' ? 'answering' : phaseForClosed(item.status),
  selectedOptionKeys: [],
  typedText: '',
  ordering: [],
  feedbackText: null,
  feedbackTone: 'neutral',
  attemptsRemaining: item.attemptsRemaining,
});

/** `solved` and anything else closed are different only in what a screen may say about them. */
const phaseForClosed = (status: string): TaskPhase => (status === 'solved' ? 'solved' : 'closed');

export const acceptsInput = (state: TaskState): boolean =>
  state.phase === 'answering' || state.phase === 'retrying';

export const canSubmit = (state: TaskState): boolean => {
  if (!acceptsInput(state)) return false;

  const options = state.item.options ?? [];

  switch (state.item.presentation) {
    case 'multiple_choice':
    case 'grid_selection':
      return state.selectedOptionKeys.length > 0;
    case 'text_answer':
      return state.typedText.trim().length > 0;
    case 'ordering':
    case 'sequence_recall':
      return state.ordering.length === options.length && options.length > 0;
    default:
      return (
        state.selectedOptionKeys.length > 0 || state.typedText.trim().length > 0 || state.ordering.length > 0
      );
  }
};

export const isClosed = (state: TaskState): boolean => state.phase === 'solved' || state.phase === 'closed';

/**
 * The whole session. `tasks` grows when a replacement arrives, which is honest: there genuinely is one
 * more task, and the progress rail gains a segment.
 */
export type SessionState = {
  sessionId: string;
  /**
   * The number of slots the session promised, which does not move.
   *
   * `tasks` grows when a replacement arrives — that is real work, but it is a second attempt at a slot
   * already counted, and showing it turned „Úloha 5 z 9" into „Úloha 5 z 10" mid-session (R0091).
   */
  plannedCount: number;
  headline: string;
  inCalibration: boolean;
  tasks: TaskState[];
  currentIndex: number;
  isComplete: boolean;
  completionMessage: string | null;
  rewardMessage: string | null;
  starsEarned: number;
  coinsEarned: number;
  /**
   * What finishing this session earned. Arrives on the answer that completes it, so it is empty until
   * then — and stays empty for most sessions, because most cross no threshold.
   */
  earnedAchievements: EarnedAchievement[];
};

export const currentTask = (session: SessionState): TaskState | null =>
  session.tasks[session.currentIndex] ?? null;

export const closedCount = (session: SessionState): number => session.tasks.filter(isClosed).length;

/**
 * Which task the child is on, counted in slots, from one.
 *
 * Read from the task in hand rather than from how many are closed. Answering closes a task, so a rail
 * counting closed tasks jumped forward on „Skontrolovať" while the child was still looking at the task
 * they had just answered — it must move on „Ďalšia úloha", which is what advances `currentIndex` (R0091).
 */
export const position = (session: SessionState): number => {
  const task = currentTask(session);

  return (task === null ? session.currentIndex : task.item.slotIndex) + 1;
};

export const sessionStateFrom = (session: TrainingSession): SessionState => {
  const items = session.items ?? [];
  const firstOpen = items.findIndex((item: SessionItem): boolean => item.status === 'open');

  return {
    sessionId: session.sessionId,
    plannedCount: session.plannedInstanceCount > 0 ? session.plannedInstanceCount : items.length,
    headline: session.message,
    inCalibration: session.inCalibration,
    tasks: items.map(taskStateFor),
    currentIndex: firstOpen >= 0 ? firstOpen : 0,
    // Nothing left open means the day is done. The API's own sentence carries what to say about it —
    // "Dnes si už trénoval. Chceš si dať ešte jedno kolo navyše?" — and the client must not invent one.
    isComplete: items.length > 0 && firstOpen < 0,
    completionMessage: items.length > 0 && firstOpen < 0 ? session.message : null,
    rewardMessage: null,
    starsEarned: 0,
    coinsEarned: 0,
    earnedAchievements: [],
  };
};
